import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Card, CardBody, CardHeader, Spinner } from 'reactstrap';
import api from '~/services/api';

const EVENT_ICONS = {
  conversation_started: 'fab fa-whatsapp',
  template_sent: 'las la-paper-plane',
  client_accepted: 'las la-check-circle',
  client_rejected: 'las la-times-circle',
  delivery_confirmed: 'las la-check-circle',
  delivery_problem: 'las la-exclamation-triangle',
  support_requested: 'las la-headset',
  payment_link_sent: 'las la-link',
  payment_received: 'las la-clock',
  payment_analysis: 'las la-shield-alt',
  payment_approved: 'las la-check',
  delivery_started: 'las la-truck',
  order_cancelled: 'las la-times-circle',
  handoff: 'las la-users',
  conversation_closed: 'las la-check-square',
  score_received: 'las la-star',
};

const EVENT_COLORS = {
  success: '#60b05d',
  info: '#17a2b8',
  danger: '#dc3545',
  warning: '#ffc107',
  secondary: '#6c757d',
};

function getConversationStatusLabel(conversation) {
  if (!conversation) return '';
  if (conversation.status === 'closed') return 'Finalizado';
  if (conversation.is_handoff) return 'Em Atendimento';
  return 'Ativo';
}

function getStatusBadgeStyle(conversation) {
  if (!conversation) {
    return { backgroundColor: '#e9ecef', color: '#6c757d' };
  }
  if (conversation.status === 'closed') {
    return { backgroundColor: '#e9ecef', color: '#6c757d' };
  }
  if (conversation.is_handoff) {
    return { backgroundColor: '#fff3cd', color: '#856404' };
  }
  return { backgroundColor: '#d4edda', color: '#155724' };
}

function getMessageStatusIcon(msg) {
  if (msg.status === 'read') return '✓✓';
  if (msg.status === 'delivered') return '✓✓';
  if (msg.status === 'sent') return '✓';
  return '⏳';
}

function getMediaIcon(mediaType) {
  if (mediaType === 'image') return 'las la-image';
  if (mediaType === 'video') return 'las la-video';
  if (mediaType === 'audio') return 'las la-microphone';
  return 'las la-file-alt';
}

function getMediaLabel(mediaType) {
  if (mediaType === 'image') return 'Imagem';
  if (mediaType === 'video') return 'Vídeo';
  if (mediaType === 'audio') return 'Áudio';
  return 'Documento';
}

function WhatsappTimeline({ cartId, hasWhatsappFlow }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMessages, setShowMessages] = useState(false);

  useEffect(() => {
    // Sem fluxo WhatsApp na venda não há timeline: evita a chamada.
    if (!cartId || !hasWhatsappFlow) {
      setLoading(false);
      return;
    }

    api
      .get(`/whatsapp/cart/${cartId}/timeline`)
      .then(response => {
        setData(response.data);
      })
      .catch(err => {
        console.error('Erro ao carregar timeline WhatsApp:', err);
        setData({ has_whatsapp: false });
      })
      .finally(() => setLoading(false));
  }, [cartId, hasWhatsappFlow]);

  const statusLabel = useMemo(
    () => getConversationStatusLabel(data?.conversation),
    [data]
  );

  const statusBadgeStyle = useMemo(
    () => getStatusBadgeStyle(data?.conversation),
    [data]
  );

  const handleToggleMessages = useCallback(() => {
    setShowMessages(prev => !prev);
  }, []);

  if (loading) {
    return (
      <Card className="new-layout card mb-3">
        <CardBody className="text-center py-4">
          <Spinner size="sm" color="success" /> Carregando WhatsApp...
        </CardBody>
      </Card>
    );
  }

  if (!data || !data.has_whatsapp) {
    return null;
  }

  return (
    <Card className="new-layout card mb-3" style={{ overflow: 'hidden' }}>
      <CardHeader
        className="d-flex align-items-center justify-content-between"
        style={{
          backgroundColor: 'rgba(96, 176, 93, 0.15)',
          borderBottom: '1px solid rgba(96, 176, 93, 0.25)',
        }}
      >
        <div className="d-flex align-items-center">
          <i
            className="fab fa-whatsapp mr-2"
            style={{ fontSize: '1.2rem', color: '#60b05d' }}
          />
          <strong style={{ color: '#60b05d' }}>Fluxo WhatsApp</strong>
        </div>
        {data.conversation && (
          <span
            style={{
              ...statusBadgeStyle,
              fontSize: '0.7rem',
              fontWeight: 600,
              padding: '3px 10px',
              borderRadius: 12,
              lineHeight: 1.4,
            }}
          >
            {statusLabel}
          </span>
        )}
      </CardHeader>
      <CardBody className="px-3 py-3">
        {/* Timeline de Eventos */}
        <div className="whatsapp-timeline">
          {data.events.map(event => (
            <div
              key={`${event.type}-${event.timestamp}`}
              className="d-flex align-items-start mb-3"
            >
              <div
                className="d-flex align-items-center justify-content-center mr-3"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor:
                    EVENT_COLORS[event.color] || EVENT_COLORS.secondary,
                  color: '#fff',
                  flexShrink: 0,
                }}
              >
                <i
                  className={EVENT_ICONS[event.type] || 'las la-circle'}
                  style={{ fontSize: '0.9rem' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div
                  className="font-weight-600"
                  style={{ fontSize: '0.85rem' }}
                >
                  {event.label}
                </div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  {event.description}
                </div>
                <div
                  className="text-muted"
                  style={{ fontSize: '0.7rem', marginTop: 2 }}
                >
                  {new Date(event.timestamp).toLocaleString('pt-BR')}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Toggle para mostrar histórico de mensagens */}
        {data.messages && data.messages.length > 0 && (
          <>
            <hr />
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary w-100"
              onClick={handleToggleMessages}
              style={{
                boxShadow: 'none',
                outline: 'none',
                border: '1px solid #dee2e6',
              }}
            >
              {showMessages
                ? 'Ocultar histórico de mensagens'
                : `Ver histórico de mensagens (${data.messages.length})`}
            </button>

            {showMessages && (
              <div
                className="mt-3"
                style={{
                  maxHeight: 400,
                  overflowY: 'auto',
                  padding: '10px',
                  backgroundColor: '#ECE5DD',
                  borderRadius: 8,
                  position: 'relative',
                }}
              >
                {data.messages.map(msg => (
                  <div
                    key={`msg-${msg.id}`}
                    className={`d-flex mb-2 ${
                      msg.direction === 'outbound'
                        ? 'justify-content-end'
                        : 'justify-content-start'
                    }`}
                  >
                    <div
                      style={{
                        maxWidth: '75%',
                        padding: '8px 12px',
                        borderRadius: 8,
                        backgroundColor:
                          msg.direction === 'outbound' ? '#DCF8C6' : '#fff',
                        boxShadow: '0 1px 1px rgba(0,0,0,0.1)',
                        fontSize: '0.8rem',
                      }}
                    >
                      {/* Imagem */}
                      {msg.media_type === 'image' && msg.media_url && (
                        <a
                          href={msg.media_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={msg.media_url}
                            alt="Imagem"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '180px',
                              borderRadius: '6px',
                              marginBottom: '4px',
                            }}
                          />
                        </a>
                      )}

                      {/* Vídeo */}
                      {msg.media_type === 'video' && msg.media_url && (
                        <div style={{ marginBottom: '4px' }}>
                          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                          <video
                            src={msg.media_url}
                            controls
                            style={{
                              maxWidth: '100%',
                              maxHeight: '180px',
                              borderRadius: '6px',
                            }}
                          />
                        </div>
                      )}

                      {/* Áudio */}
                      {msg.media_type === 'audio' && msg.media_url && (
                        <div style={{ marginBottom: '4px' }}>
                          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                          <audio
                            src={msg.media_url}
                            controls
                            style={{ width: '100%', maxWidth: '250px' }}
                          />
                        </div>
                      )}

                      {/* Documento */}
                      {msg.media_type === 'document' && msg.media_url && (
                        <div style={{ marginBottom: '4px' }}>
                          <a
                            href={msg.media_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 10px',
                              background: 'rgba(0,0,0,0.04)',
                              borderRadius: '6px',
                              textDecoration: 'none',
                              color: '#0C447C',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                            }}
                          >
                            <i
                              className="las la-file-alt"
                              style={{ fontSize: '16px' }}
                            />
                            {msg.media_name || 'Documento'}
                          </a>
                        </div>
                      )}

                      {/* Mídia sem URL (não foi baixada) */}
                      {msg.media_type && !msg.media_url && (
                        <div
                          style={{
                            padding: '8px 12px',
                            background: 'rgba(0,0,0,0.04)',
                            borderRadius: '6px',
                            marginBottom: '4px',
                            fontSize: '0.75rem',
                            color: '#888',
                            textAlign: 'center',
                          }}
                        >
                          <i
                            className={getMediaIcon(msg.media_type)}
                            style={{
                              fontSize: '20px',
                              display: 'block',
                              marginBottom: '2px',
                            }}
                          />
                          {getMediaLabel(msg.media_type)}
                        </div>
                      )}

                      {/* Texto */}
                      {msg.payload && (
                        <div
                          style={{
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-line',
                          }}
                        >
                          {msg.payload}
                        </div>
                      )}

                      {/* Hora + status */}
                      <div
                        className="text-right text-muted"
                        style={{ fontSize: '0.65rem', marginTop: 4 }}
                      >
                        {new Date(msg.created_at).toLocaleString('pt-BR')}
                        {msg.direction === 'outbound' && (
                          <span className="ml-1">
                            {getMessageStatusIcon(msg)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {showMessages && data.messages.length > 5 && (
              <div
                className="text-center text-muted mt-1"
                style={{ fontSize: '0.7rem' }}
              >
                <i className="las la-angle-down mr-1" />
                Role para ver mais mensagens
                <i className="las la-angle-down ml-1" />
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}

export default WhatsappTimeline;
